'use client';
import { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import { GetSeriousApiClient, createLocalApiClient } from '@/lib/api';

interface ApiContextType {
  apiClient: GetSeriousApiClient;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

interface ApiProviderProps {
  children: ReactNode;
  onUserSync?: (user: any) => void;
}

export const ApiProvider = ({ children, onUserSync }: ApiProviderProps) => {
  // Create the API client once and memoize it
  // Token persistence is handled internally by the client
  const apiClient = useMemo(
    () => createLocalApiClient(),
    []
  );

  // Sync user state on mount if client has a token
  // Also refresh the auth token to ensure it's valid
  useEffect(() => {
    const syncUser = async () => {
      if (apiClient.isAuthenticated() && onUserSync) {
        try {
          // First, attempt to refresh the token
          await apiClient.auth.refresh();
          
          // Then fetch the current user with the refreshed token
          const user = await apiClient.auth.getCurrentUser();
          onUserSync(user);
        } catch (error) {
          console.error('Failed to sync user on mount:', error);
          // If token refresh or user fetch fails, clear the token
          apiClient.clearAuthToken();
          onUserSync(null);
        }
      }
    };

    syncUser();
  }, [apiClient, onUserSync]);

  return (
    <ApiContext.Provider value={{ apiClient }}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): GetSeriousApiClient => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context.apiClient;
};
